export type AnalyticsEvent =
  | 'hero_join_beta_clicked'
  | 'hero_demo_clicked'
  | 'beta_form_started'
  | 'beta_form_submitted'
  | 'beta_registration_success'
  | 'beta_registration_duplicate'
  | 'beta_registration_error'
  | 'language_changed'
  | 'supported_hardware_clicked'
  | 'faq_opened'

type AnalyticsConsent = 'accepted' | 'rejected'
type AnalyticsProvider = 'ga4'

const consentKey = 'tonesignature.analyticsConsent'
const cookieSettingsEvent = 'tonesignature:open-cookie-settings'
const provider = (import.meta.env.VITE_ANALYTICS_PROVIDER || 'ga4') as AnalyticsProvider
const analyticsId = import.meta.env.VITE_ANALYTICS_ID as string | undefined

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let initialized = false

export function getAnalyticsConsent(): AnalyticsConsent | null {
  const stored = window.localStorage.getItem(consentKey)
  return stored === 'accepted' || stored === 'rejected' ? stored : null
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(cookieSettingsEvent))
}

export function onCookieSettingsOpen(callback: () => void) {
  window.addEventListener(cookieSettingsEvent, callback)
  return () => window.removeEventListener(cookieSettingsEvent, callback)
}

export function setAnalyticsConsent(consent: AnalyticsConsent) {
  window.localStorage.setItem(consentKey, consent)

  if (consent === 'accepted') {
    initializeAnalytics()
  }
}

export function initializeAnalytics() {
  if (initialized || getAnalyticsConsent() !== 'accepted' || !analyticsId || provider !== 'ga4') {
    return
  }

  initialized = true
  window.dataLayer = window.dataLayer ?? []
  window.gtag = window.gtag ?? function gtag(...args: unknown[]) {
    window.dataLayer?.push(args)
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(analyticsId)}`
  document.head.appendChild(script)

  window.gtag('js', new Date())
  window.gtag('config', analyticsId, { anonymize_ip: true })
}

export function trackEvent(eventName: AnalyticsEvent, parameters?: Record<string, string | number | boolean>) {
  if (getAnalyticsConsent() !== 'accepted') {
    return
  }

  initializeAnalytics()
  window.gtag?.('event', eventName, parameters ?? {})
}
